import { useMemo, useState } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

import usePartMutations from "../../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Modal from "../../../layouts/Modal";
import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import EditListingSpecifics from "../EditListingSpecifics";
import RecentlyListedErrorCell from "../recentlyListed/RecentlyListedErrorCell";
import {
  getListingWorkflowSteps,
} from "./listingWorkflowStatus";
import UnlistedReadyStatus from "./UnlistedReadyStatus";


type Props = {
  part: IPart;
};

export default function UnlistedListingWorkflowCell({ part }: Props) {
  const steps = useMemo(() => getListingWorkflowSteps(part), [part]);
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Title Configured");
  const [specificsOpen, setSpecificsOpen] = useState(false);

  const handleTitleConfiguredToggle = () => {
    const next = !part.titleConfigured;

    patchPart.mutate(
      {
        id: part.id,
        titleConfigured: next,
      },
      {
        ...saveHandlers,
        onSuccess: () => {
          part.titleConfigured = next;
          saveHandlers.onSuccess();
        },
      }
    );
  };

  return (
    <div
      className="flex h-full w-full min-w-0 flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <Modal
        key={`aspects-workflow-${part.id}`}
        open={specificsOpen}
        setOpen={setSpecificsOpen}
        width="max-w-7xl"
      >
        {specificsOpen && <EditListingSpecifics part={part} />}
      </Modal>
      <div className="flex h-full flex-col rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white px-3 py-2.5 shadow-sm ring-1 ring-slate-200/50">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          Listing workflow
        </p>
        {part.batchItem && (
          <div className="mb-2 border-b border-amber-200 pb-2">
            <RecentlyListedErrorCell
              batchItem={part.batchItem}
              showExtraInfo={false}
            />
          </div>
        )}

        <ul
          className="flex w-full min-w-0 flex-col gap-0.5"
          aria-label="Listing workflow checklist"
        >
          {steps.map((step) => {
            const done = step.done;
            const isTitleConfiguredStep = step.id === "title";
            const content = (
              <>
                {done ? (
                  <CheckCircleIcon
                    className="mt-px h-4 w-4 shrink-0 text-emerald-600"
                    aria-hidden
                  />
                ) : (
                  <span
                    className="mt-px h-4 w-4 shrink-0 rounded-full border-[1.5px] border-slate-300 bg-white"
                    aria-hidden
                  />
                )}
                <span className="flex min-w-0 flex-col">
                  <span
                    className={classNames(
                      "min-w-0 truncate text-[13px] leading-snug",
                      done ? "text-slate-900" : "text-slate-600"
                    )}
                  >
                    {step.label}
                  </span>
                  {!done && step.detail && (
                    <span className="line-clamp-2 text-[11px] leading-snug text-amber-800/90">
                      {step.detail}
                    </span>
                  )}
                </span>
              </>
            );

            return (
              <li key={step.id} className="flex items-start gap-1">
                {isTitleConfiguredStep ? (
                  <button
                    type="button"
                    onClick={handleTitleConfiguredToggle}
                    disabled={patchPart.isLoading}
                    title={!done && step.detail ? step.detail : step.label}
                    aria-pressed={done}
                    aria-label={
                      !done && step.detail
                        ? `${step.label}: not ticked. ${step.detail} Click to change.`
                        : `${step.label}: ${done ? "ticked" : "not ticked"}. Click to change.`
                    }
                    className={classNames(
                      "flex min-w-0 flex-1 items-start gap-1.5 rounded-md px-1 py-0.5 text-left transition",
                      "hover:bg-slate-100/90 focus:outline-none focus:ring-2 focus:ring-primary-500/30",
                      patchPart.isLoading && "opacity-60"
                    )}
                  >
                    {content}
                  </button>
                ) : (
                  <div
                    title={!done && step.detail ? step.detail : step.label}
                    className="flex min-w-0 flex-1 items-start gap-1.5 rounded-md px-1 py-0.5 text-left"
                  >
                    {content}
                  </div>
                )}
                {step.id === "specifics" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSpecificsOpen(true);
                    }}
                    title="Edit item specifics"
                    aria-label="Edit item specifics"
                    className="mt-px inline-flex h-6 w-6 flex-none items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <hr className="py-2 mt-2" />

        <div>
          <UnlistedReadyStatus part={part} isPill />
        </div>
      </div>
    </div>
  );
}
